import { trackApparelBrandRetailerPdpWithMixedShippingView } from "@faire/web-api/events/apparelBrand/view/retailerPdpWithMixedShipping";
import { trackCartProductPageBrandCartViewBagClick } from "@faire/web-api/events/cart/click/productPageBrandCartViewBag";
import { trackPricingProductPageBrandCartCheckoutClick } from "@faire/web-api/events/pricing/click/productPageBrandCartCheckout";
import { trackProductPageSidecartToggleClick } from "@faire/web-api/events/productPage/click/sidecartToggle";
import { trackProductsPageViaImpressionView } from "@faire/web-api/events/products/view/pageViaImpression";
import { IDwellTime } from "@faire/web-api/indigofair/data/IDwellTime";
import { IProductPageLayoutRequest } from "@faire/web-api/indigofair/data/IProductPageLayoutRequest";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import brandbrandToken from "@faire/web-api/routes/brand/brandToken";
import flashSale from "@faire/web-api/routes/flash-sale";
import { logAssert } from "@faire/web/common/logging";
import { TelemetryManager } from "@faire/web/common/opentelemetry/TelemetryManager";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import {
  ActionName,
  LoadTimingResult,
} from "@faire/web/common/performance/userPerceivedLoadTiming/types";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import { generateTracingMetadata } from "@faire/web/common/tracing/generateTracingMetadata";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Row } from "@faire/web/slate/Layout/Row";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import { usePathParams } from "@faire/web/ui/hooks/usePathParams";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import { useState } from "@faire/web/ui/hooks/useState";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react";
import React, { Suspense, useCallback, useEffect, useRef } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import {
  recordLandingActivity,
  recordSignupActivity,
} from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { useNextPrefetchContext } from "@faire/retailer/app/_lib/routing/NextPrefetchContext";
import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer/components/AppSignUp/AppSignUpPageResultsProvider";
import { RetailerFooter } from "@faire/retailer/components/Footer/RetailerFooter";
import { AddedToBagContainer } from "@faire/retailer/components/Notifications/AddedToBag/AddedToBagStore";
import { useAddedToBag } from "@faire/retailer/components/Notifications/AddedToBag/useAddedToBag";
import { UserFeedbackHiddenToast } from "@faire/retailer/components/UserFeedback/UserFeedbackHiddenToast";
import {
  AddToCart,
  PageInitialized,
  ViewProduct,
} from "@faire/retailer/events";
import {
  QuickAddModalContext,
  QuickAddModalEvent,
} from "@faire/retailer/features/ProductQuickAddModal/QuickAddModalContext";
import { useCartUpdate } from "@faire/retailer/hooks/cart/useCartUpdate";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { isOnlineOnlyRestricted } from "@faire/retailer/lib/brandRestrictionUtils";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { useIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { requestIdleCallbackOrFallback } from "@faire/retailer/lib/requestIdleCallbackOrFallback";
import {
  BrandOrderReviewPageNavigationSourceContext,
  BrandOrderReviewPageNavigationSource,
} from "@faire/retailer/lib/useBrandOrderReviewPageNavigationSourceContext";
import { useDwellService } from "@faire/retailer/services/DwellService";
import {
  trackEditCartItemEvent,
  trackProductClick,
} from "@faire/retailer/services/Events";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import { assignSettingProductPageReactQuery } from "@faire/retailer/settings/getSettingProductPageReactQuery";
import {
  useAssignSettingAdjustMoreFromBrandLoadingOffset,
  useSettingAdjustMoreFromBrandLoadingOffset,
} from "@faire/retailer/settings/useSettingAdjustMoreFromBrandLoadingOffset";
import { useAssignSettingChangeBagToCartCopyAndIcon } from "@faire/retailer/settings/useSettingChangeBagToCartCopyAndIcon";
import { useSettingEnableUserFeedback } from "@faire/retailer/settings/useSettingEnableUserFeedback";
import {
  useAssignSettingFaireDirectPDPSignup,
  useSettingFaireDirectPDPSignup,
} from "@faire/retailer/settings/useSettingFaireDirectPDPSignup";
import { useSettingProductPageReactQuery } from "@faire/retailer/settings/useSettingProductPageReactQuery";
import { ProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import {
  RetailerStore,
  useRetailerStore,
} from "@faire/retailer/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";
import { AppModalStore } from "@faire/retailer/stores/ui/AppModalStore";
import { useAppSnackbarStore } from "@faire/retailer/stores/ui/AppSnackbarStore";
import { EmployeeProductActions } from "@faire/retailer/views/product/__internal__/Product/EmployeeProductActions";
import { ProductModals } from "@faire/retailer/views/product/__internal__/Product/ProductModals";
import { RelatedSearchList } from "@faire/retailer/views/product/__internal__/Product/RelatedSearchList";
import { ReviewsSection } from "@faire/retailer/views/product/__internal__/Product/ReviewsSection";
import { ProductHeader } from "@faire/retailer/views/product/__internal__/ProductHeader/ProductHeader";
import { ProductStickyBrandHeader } from "@faire/retailer/views/product/__internal__/ProductHeader/ProductStickyBrandHeader";
import { ProductLoadingSkeleton } from "@faire/retailer/views/product/__internal__/ProductLoadingSkeleton";
import {
  BrandInfoRowFallback,
  LazyBrandInfoRow,
} from "@faire/retailer/views/product/BrandInfoRow";
import { LazyBrandValuesSection } from "@faire/retailer/views/product/BrandValuesSection";
import { LazyProductLayoutElement } from "@faire/retailer/views/product/ProductLayoutElements";
import { useProductState } from "@faire/retailer/views/product/useProductState";
import {
  getProductDescription,
  getProductDetails,
  getProductName,
  isMixedProduct,
} from "@faire/retailer/views/product/utils/productUtils";

import { BrandCartEventHandler, BrandCartEvents } from "../brand/BrandCart";
import { BrandCartViewState } from "../brand/BrandCart/BrandCartViewState";
import { LazyBrandCart } from "../brand/BrandCart/lazyBrandCart";
import { BrandPageCartStateContext } from "../brand/contexts/BrandPageCartStateContext";
import { usePreloadBuyItAgainAndImages } from "../brand/hooks/useFetchCachedBrandView";
import { preloadBrandPageLoggedIn } from "../brand/preloadBrandPageLoggedIn";
import { shouldShowRscLibp } from "../brand/utils/shouldShowRscLibp";

import { AnimatedFaireDirectSignUpBanner } from "./Components/AnimatedFaireDirectSignUpBanner";
import { SEOProductInfo } from "./Components/SEOProductInfo";
import { ProductContent } from "./Content";
import { LazyNotFoundPlaceholder } from "./NotFoundPlaceholder";
import { ProductState } from "./ProductState";
import { generateBrandPageQueryObjForProductPage } from "./utils/generateBrandUrlForProductPage";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

const ProductBase: React.FC = () => {
  const isBrandPreview = useIsBrandPreview();
  const productPageReactQuerySetting = useSettingProductPageReactQuery();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const name: ActionName = isLoggedInRetailerOrBrandPreview
    ? "page-product-logged-in"
    : "page-product-logged-out";
  TelemetryManager.get().startTrace(`uplt-${name}`);

  const { productToken } = usePathParams("/product/productToken");
  const queryParams = useQueryParams((s) => parseQueryParams(s ?? ""));

  const { current: legacyProductState } = useRefFrom(() => {
    if (productPageReactQuerySetting) {
      return undefined;
    }
    const response =
      ProductCacheStore.get().prefetchedLeanProduct(productToken);
    return new ProductState(productToken, response);
  });
  const reactQueryProductState = useProductState(productToken);
  const productState = productPageReactQuerySetting
    ? reactQueryProductState
    : legacyProductState;
  logAssert(!!productState, "productState is undefined");

  const assignSettingChangeBagToCartCopyAndIcon =
    useAssignSettingChangeBagToCartCopyAndIcon();

  useEffect(() => {
    assignSettingChangeBagToCartCopyAndIcon();
  }, [assignSettingChangeBagToCartCopyAndIcon]);

  useEffect(() => {
    assignSettingProductPageReactQuery();
  }, []);

  useEffect(() => {
    legacyProductState?.init();
  }, [legacyProductState]);

  useEffect(() => {
    PerformanceMarker.get().mark(PageLoadEvent.pageStart);
  }, []);

  useAppSignUpPageResultsUpdateEffect({ pdpProduct: productState.product });

  const { retailerToken } = useRetailerStore(["retailerToken"]);
  const trackEventToken = queryParams.get("trackEventToken");
  useEffect(() => {
    const eventPath = `/retailer/${retailerToken}`;
    // IMPORTANT - Do not remove. BE uses this to calculate product page visits.
    trackEvent(`${eventPath}/product-page/${productToken}`);
    if (trackEventToken) {
      trackEvent(
        `${eventPath}/product-page/${productToken}/${trackEventToken}`
      );
    }
  }, [productToken, trackEventToken, retailerToken]);

  const autoSignUpTimeoutHandleRef = useRef<number | undefined>(undefined);

  const queryParamSignUp = queryParams.get("signUp");

  useEffect(() => {
    // Handle the case when there is another sign-up modal shown before our timeout executes.
    // If the user already closed the modal, we want to react to the the query param changing,
    // and cancel the timer so we don't show the sign-up modal again and annoy the user.
    clearTimeout(autoSignUpTimeoutHandleRef.current);
  }, [queryParamSignUp]);

  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();
  const settingFaireDirectPDPSignup = useSettingFaireDirectPDPSignup();
  const enableUserFeedback = useSettingEnableUserFeedback();
  useEffect(() => {
    if (isLoggedInRetailerOrBrandPreview || settingFaireDirectPDPSignup) {
      return;
    }

    if (isFaireDirectActiveInLoggedOut) {
      autoSignUpTimeoutHandleRef.current = setTimeoutIfPossible(() => {
        if (!AppModalStore.get().isModalShowing) {
          popSignUp(SignUpType.FAIRE_DIRECT, () => null, {
            element: SignUpElement.AUTO_POPUP,
            sourceId: "faire_direct_auto_signup",
          });
        }
      }, 5000);
    }

    return () => {
      clearTimeout(autoSignUpTimeoutHandleRef.current);
    };
  }, [
    isFaireDirectActiveInLoggedOut,
    isLoggedInRetailerOrBrandPreview,
    settingFaireDirectPDPSignup,
  ]);

  const {
    brandReviewSummary,
    isLoading,
    clientProductResponseDataUpdatedAt,
    product,
    brand,
    isBrandActive,
    badges,
    importShipmentInfo,
    infoTexts,
    disclaimers,
    productAttributeTags,
    videos,
    showroomData,
    samplesBannerDetails,
    productRelatedSearches,
    productReviewSummary,
  } = productState;

  const viewport = useViewport();

  const { userToken } = useUserStore(["userToken"]);

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name,
    pageLoadKey: name,
    isPageLoading: isLoading,
    dataUpdatedAt: clientProductResponseDataUpdatedAt,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
    trackResultCallback: (name, status, loadType, result: LoadTimingResult) => {
      const metadata = generateTracingMetadata({
        name,
        loadType,
        status,
        result,
        userToken,
        isDesktopAndAbove: viewport.isDesktopAndAbove,
        isMobile: viewport.isMobile,
      });

      TelemetryManager.get().endTrace(`uplt-${name}`, metadata);
    },
  });

  const containerRef = React.createRef<HTMLDivElement>();
  const [brandCartViewState, setBrandCartViewState] = useState<
    BrandCartViewState | undefined
  >(undefined);
  const numberOfItemsInCart = brandCartViewState?.numberOfItemsInCart;
  const { ignoreContainer } = useAddedToBag();
  const [showSideCart, setShowSideCart] = useState(false);

  const setShowSideCartDynamic = () => {
    if (showSideCart) {
      trackProductPageSidecartToggleClick(
        "close",
        numberOfItemsInCart?.toString() ?? ""
      );
      ignoreContainer(AddedToBagContainer.BRAND_CART, false);
    } else {
      trackProductPageSidecartToggleClick(
        "open",
        numberOfItemsInCart?.toString() ?? ""
      );
      ignoreContainer(AddedToBagContainer.BRAND_CART, true);
    }
    setShowSideCart(!showSideCart);
  };

  const assignSettingFaireDirectPDPSignup =
    useAssignSettingFaireDirectPDPSignup();
  useEffect(() => {
    if (!isLoggedInRetailerOrBrandPreview && isFaireDirectActiveInLoggedOut) {
      assignSettingFaireDirectPDPSignup();
    }
  }, [
    assignSettingFaireDirectPDPSignup,
    isFaireDirectActiveInLoggedOut,
    isLoggedInRetailerOrBrandPreview,
  ]);

  const enableSideCart = viewport.isDesktopAndAbove && canAddToCart();

  // We still have minor M&M traffic, keeping this for safety
  if (productToken.startsWith("mmp")) {
    return <Redirect to={flashSale()} />;
  } else if (isLoading) {
    return <ProductLoadingSkeleton />;
  } else if (product && brand) {
    return (
      <>
        <Container>
          <SEOProductInfo
            productName={product.name?.translated ?? product.name?.original}
            productDescription={getProductDescription(product)}
            brandCompanyName={brand.name}
          />
          <Row align="flex-start">
            <ProductWrapper ref={containerRef}>
              <Wrapper>
                <ProductHeader
                  brand={brand}
                  product={product}
                  hideBreadcrumbs={enableSideCart}
                />
              </Wrapper>
              {enableSideCart ? (
                <ProductStickyBrandHeader
                  product={product}
                  brand={brand}
                  containerRef={containerRef}
                  brandReviewSummary={brandReviewSummary}
                  showShowroomBadge={
                    !!showroomData?.isMidPricePointApparelProduct
                  }
                  showSidecart={showSideCart}
                  setCart={setShowSideCartDynamic}
                  numberOfProductsInCart={numberOfItemsInCart}
                  cartTotal={brandCartViewState?.cartBrand?.total}
                />
              ) : null}
              <Wrapper>
                <ProductContent
                  productToken={productToken}
                  brand={brand}
                  product={product}
                  badges={badges}
                  brandReviewSummary={brandReviewSummary}
                  importShipmentInfo={importShipmentInfo}
                  infoTexts={infoTexts}
                  showroomData={showroomData}
                  disclaimers={disclaimers}
                  preselectedProductOptionToken={
                    queryParams.get("productOptionToken") ?? undefined
                  }
                  productAttributeTags={productAttributeTags}
                  productVideos={videos}
                  samplesBannerDetails={samplesBannerDetails}
                  loadTimingRef={loadTimingRef}
                  showSideCart={enableSideCart && showSideCart}
                  afterAddToCart={
                    enableSideCart ? brandCartViewState?.refreshCart : undefined
                  }
                />
              </Wrapper>
              <MoreFromBrand brand={brand} product={product} />
              <BrandOrderReviewPageNavigationSourceContext.Provider
                value={BrandOrderReviewPageNavigationSource.product_page}
              >
                <ReviewsSection
                  brand={brand}
                  product={product}
                  productReviewSummary={productReviewSummary}
                  brandReviewSummary={brandReviewSummary}
                />
              </BrandOrderReviewPageNavigationSourceContext.Provider>
              {enableUserFeedback ? (
                <UserFeedbackHiddenToast brandToken={brand?.token} />
              ) : null}
            </ProductWrapper>
            {enableSideCart ? (
              <DynamicBrandCart
                brandToken={brand.token ?? ""}
                setCartViewState={setBrandCartViewState}
                showSidecart={showSideCart}
                data-test-id="brand-cart-column"
              />
            ) : null}
          </Row>
          <ProductWrapper>
            {!isLoggedInRetailerOrBrandPreview ? (
              <WhenVisibilityIsImminent>
                <Suspense fallback={null}>
                  <LazyBrandValuesSection productToken={productToken} />
                </Suspense>
              </WhenVisibilityIsImminent>
            ) : null}
            <QuickAddModalContext.Provider
              value={{
                onEvent: (event) => {
                  if (
                    enableSideCart &&
                    event === QuickAddModalEvent.ADD_TO_CART
                  ) {
                    brandCartViewState?.refreshCart();
                  }
                },
              }}
            >
              <ProductLayoutElements productToken={productToken} />
            </QuickAddModalContext.Provider>
            <RelatedSearchList
              productRelatedSearches={productRelatedSearches}
            />
            <ProductModals
              productToken={productToken}
              brand={brand}
              brandToken={brand?.token}
              updateCartFn={brandCartViewState?.updateItems}
              queryParams={queryParams}
            />
            <EmployeeProductActions
              product={product}
              isBrandActive={isBrandActive}
            />
          </ProductWrapper>
        </Container>
        {isLoading || isBrandPreview ? null : <RetailerFooter />}
        {/* The signup banner must be kept as the last visible component on the page.
        It has padding that must be added to the bottom of the page, below the footer. */}
        {settingFaireDirectPDPSignup ? (
          <AnimatedFaireDirectSignUpBanner />
        ) : null}
        <ProductPageEffects
          brand={brand}
          product={product}
          queryParams={queryParams}
        />
        <PreloadBrandPageEffects
          brandToken={brand?.token}
          productToken={productToken}
        />
      </>
    );
  } else {
    return (
      <Suspense fallback={<ProductLoadingSkeleton />}>
        <LazyNotFoundPlaceholder />
      </Suspense>
    );
  }
};

export const Product = observer(ProductBase);
Product.displayName = "ProductPage";

const Container = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;

  ${responsiveMediaQueries.mobile} {
    padding-bottom: ${getSpacing("10x")};
  }
`;
const ProductWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
`;

const Wrapper = styled.div`
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "6x")};
  }
`;

const PreloadBrandPageEffects: React.FC<{
  brandToken: string | undefined;
  productToken: string;
}> = ({ brandToken, productToken }) => {
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const queryClient = useQueryClient();

  const nextPrefetch = useNextPrefetchContext();

  usePreloadBuyItAgainAndImages(brandToken ?? "");

  useEffect(() => {
    if (isLoggedInRetailerOrBrandPreview && brandToken) {
      requestIdleCallbackOrFallback(() => {
        if (shouldShowRscLibp()) {
          nextPrefetch(brandbrandToken(brandToken));
        } else {
          const queryParams =
            generateBrandPageQueryObjForProductPage(productToken);
          preloadBrandPageLoggedIn(brandToken, queryParams, queryClient);
        }
      });
    }
  }, [
    brandToken,
    productToken,
    queryClient,
    isLoggedInRetailerOrBrandPreview,
    nextPrefetch,
  ]);

  return null;
};

const DynamicBrandCart: React.FC<{
  brandToken: string;
  setCartViewState: (brandCartViewState: BrandCartViewState) => void;
  showSidecart?: boolean;
}> = ({ brandToken, setCartViewState, showSidecart }) => {
  const { updateCartItems } = useCartUpdate();
  const { current: brandCartViewState } = useRefFrom(
    () => new BrandCartViewState(brandToken, updateCartItems, false, true)
  );
  const handleCartItemsUpdatedEvent = useCallback(() => {
    brandCartViewState.refreshCart();
  }, [brandCartViewState]);

  useEffect(() => {
    AddToCart.subscribe(handleCartItemsUpdatedEvent);
    return () => {
      AddToCart.unsubscribe(handleCartItemsUpdatedEvent);
    };
  }, [handleCartItemsUpdatedEvent]);

  useEffect(() => {
    brandCartViewState.init();
    setCartViewState(brandCartViewState);
    return function unmount() {
      brandCartViewState.cleanup();
    };
  }, [brandCartViewState, setCartViewState]);

  return showSidecart ? (
    <Suspense fallback={null}>
      <BrandPageCartStateContext.Provider value={{ brandCartViewState }}>
        <LazyBrandCart brandToken={brandToken} onEvent={trackBrandCartEvents} />
      </BrandPageCartStateContext.Provider>
    </Suspense>
  ) : null;
};

const ProductPageEffects: React.FC<{
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  queryParams: QueryParams;
}> = ({ brand, product, queryParams }) => {
  const isBrandPreview = useIsBrandPreview();
  useEffect(() => {
    PageInitialized.publish();
  }, []);

  const { retailerToken } = useRetailerStore(["retailerToken"]);

  useEffect(() => {
    if (!isBrandPreview && product.token && isMixedProduct(product)) {
      trackApparelBrandRetailerPdpWithMixedShippingView(
        retailerToken ?? "",
        product.token
      );
    }
  }, [isBrandPreview, product, retailerToken]);

  useEffect(() => {
    ViewProduct.publish(getProductDetails(product));
  }, [product]);

  const { pathname } = useLocation();
  const history = useHistory();
  const recordLoggedOutActivity = useEffectEvent(() => {
    const loggedOutActivity = {
      product_token: product?.token,
      brand_token: brand?.token,
    };
    recordLandingActivity(loggedOutActivity, history);
    recordSignupActivity(loggedOutActivity, pathname);
  });
  useEffect(recordLoggedOutActivity, [recordLoggedOutActivity]);

  const impressionId = queryParams.get("impressionId");
  useEffect(() => {
    if (product.token && impressionId) {
      trackProductsPageViaImpressionView(product.token, impressionId);
    }
  }, [product.token, impressionId]);

  const { start, stop } = useDwellService(["start", "stop"]);
  useEffect(() => {
    start(product.token ?? "", IDwellTime.Type.PRODUCT);
    return () => {
      stop();
    };
  }, [start, stop, product.token]);

  const [_, setAppSnackbarStore] = useAppSnackbarStore([]);
  useEffect(() => {
    setAppSnackbarStore({ productTitle: getProductName(product) });
    return () => {
      setAppSnackbarStore({ productTitle: undefined });
    };
  }, [product, setAppSnackbarStore]);

  return null;
};

const ProductLayoutElements: React.FC<{ productToken: string }> = ({
  productToken,
}) => {
  const viewport = useViewport();
  const placeholder = <Spacer height={508} />;

  const productPageLayoutRequest: IProductPageLayoutRequest =
    IProductPageLayoutRequest.build({
      product_token: productToken,
      grid_size: viewport.isTabletAndBelow ? "MOBILE" : "DESKTOP",
    });
  return (
    <WhenVisibilityIsImminent
      forceRender={isPrerender()}
      customizePlaceholder={placeholder}
      // TODO(Rui) remove threshold once we can prefetch using hooks in Product
      threshold={-300}
    >
      <Suspense fallback={placeholder}>
        <LazyProductLayoutElement
          request={productPageLayoutRequest}
          productToken={productToken}
        />
      </Suspense>
    </WhenVisibilityIsImminent>
  );
};

const MoreFromBrand: React.FC<{
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
}> = ({ brand, product }) => {
  const assignAdjustMoreFromBrandLoadingOffset =
    useAssignSettingAdjustMoreFromBrandLoadingOffset();
  const shouldShowMoreFromBrand = !isOnlineOnlyRestricted(brand);

  useEffect(() => {
    if (shouldShowMoreFromBrand) {
      assignAdjustMoreFromBrandLoadingOffset();
    }
  }, [assignAdjustMoreFromBrandLoadingOffset, shouldShowMoreFromBrand]);

  const threshold = useSettingAdjustMoreFromBrandLoadingOffset();

  return shouldShowMoreFromBrand ? (
    <WhenVisibilityIsImminent
      forceRender={isPrerender()}
      customizePlaceholder={<BrandInfoRowFallback />}
      threshold={threshold}
    >
      <Suspense fallback={<BrandInfoRowFallback />}>
        <LazyBrandInfoRow
          brandToken={brand.token!}
          productToken={product.token!}
          brand={brand}
        />
      </Suspense>
    </WhenVisibilityIsImminent>
  ) : null;
};

const trackBrandCartEvents: BrandCartEventHandler = ({
  event,
  token,
  quantities,
  message,
  brandToken,
}) => {
  switch (event) {
    case BrandCartEvents.OPEN_PRODUCT_MODAL:
      if (token) {
        trackProductClick(token, "product_page_sidebar_cart_cart_item");
      }
      break;
    case BrandCartEvents.CHANGE_CART_QUANTITY:
      if (quantities && token) {
        const containerName =
          quantities.newValue === 0
            ? "product_page_sidebar_cart"
            : "product_page_sidebar_cart_dropdown";
        trackEditCartItemEvent(
          quantities.oldValue,
          quantities.newValue,
          containerName,
          token
        );
      }
      break;
    case BrandCartEvents.GO_TO_BAG:
      trackCartProductPageBrandCartViewBagClick(
        RetailerStore.get().retailerToken ?? "",
        brandToken ?? ""
      );

      break;
    case BrandCartEvents.CHECKOUT:
      trackPricingProductPageBrandCartCheckoutClick(brandToken ?? "");
      break;
    case BrandCartEvents.CHECKOUT_ERROR:
      trackEvent(`/product/cart/checkout-button/shake/${message}`);
      break;
  }
};
